import { InlineMath } from 'react-katex';

import { BiDotsVerticalRounded } from 'react-icons/bi';
import PinMessageIcon from '@src/assets/pin.png';

import { OPopper } from '@src/shared';
import AvatarCircle from '../AvatarCircle';
import AttachFile from '../ChatBox/AttachFile';
import { BottomSheetDrawer } from '../BottomSheetDrawer';

import { formatTimeStamp } from '@src/utils';
import { convertToLatex, formatCalculatorForChat, convertToLatexWithRate } from '@src/features/calculator/utils';

import useMessageBubble from './useMessageBubble';

import { IMessage } from '@src/types';

import './messageBubble.scss';

const MessageBubble = ({
  message,
  handleDeleteMessage,
  handlePinMessage,
  handleDeleteFileAttachment,
}: {
  message: IMessage;
  handleDeleteMessage: (messageId: string) => void;
  handlePinMessage: (messageId: string) => void;
  handleDeleteFileAttachment: (messageId: string) => void;
}) => {
  const {
    //message:
    file,
    edited,
    peer_id,
    peer_msg,
    isPinned,
    peer_name,
    metadata,
    timestamp,
    originalTimestamp,

    setIsFocused,
    toggleDrawer,
    messageActions,
    openActionsDrawer,

    bubbleRef,
    isFocused,
    fileHandlers,
    isMobileDevice,
    isEditingBubble,
    canDeleteAttachment,
  } = useMessageBubble({ handleDeleteMessage, handlePinMessage, message });

  // Calculator messages are rendered with KaTeX.
  // Converter-mode messages may include a "(Rate: ...)" suffix, which should appear on a separate line.
  // We use a structured result (mainLine + optional rateLine) to avoid encoding UI layout in the LaTeX string.
  const isCalculatorMessage = metadata?.source === 'calculator';
  const isConverterMessage = isCalculatorMessage && metadata?.mode === 'converter';

  // Split multi-line calculator messages and convert each line to LaTeX
  const calculatorContent = () => {
    if (!isCalculatorMessage) return null;

    if (isConverterMessage) {
      const { mainLine, rateLine } = convertToLatexWithRate(peer_msg);
      return (
        <div>
          <InlineMath math={mainLine} />
          {rateLine && (
            <>
              <br />
              <InlineMath math={rateLine} />
            </>
          )}
        </div>
      );
    }

    return formatCalculatorForChat(peer_msg).map((line, index) => (
      <div key={index}>
        <InlineMath math={convertToLatex(line)} />
      </div>
    ));
  };

  return (
    <div className="message-bubble-container" ref={bubbleRef}>
      <div className="message-bubble-avatar">
        <AvatarCircle peerId={peer_id} avatarImgSize={32} />
        {isPinned && <img src={PinMessageIcon} alt="Icon" className="pin-message" />}
      </div>

      <div className={`message-content ${edited ? 'is-edited' : ''}`}>
        <div className="message-header">
          <span className="message-bubble-name">{peer_name}</span>
          {edited && <span className="edited-label">(edited)</span>}
          <div className="message-box">
            <span className="message-bubble-time">{formatTimeStamp(originalTimestamp || timestamp)}</span>
            {isMobileDevice ? (
              <div className={`message-menu ${isFocused ? 'menu-active' : ''}`} onClick={toggleDrawer}>
                <BiDotsVerticalRounded size={24} />
              </div>
            ) : (
              <OPopper
                customStyles={{ root: { width: '180px' } }}
                position="bottom-end"
                icon={
                  <div className={`message-menu ${isFocused ? 'menu-active' : ''}`}>
                    <BiDotsVerticalRounded size={24} />
                  </div>
                }
                onClose={() => setIsFocused(false)}
                onOpen={() => setIsFocused(true)}
              >
                {messageActions.map(({ action, label }) => (
                  <div key={label} className="menu-item" onClick={action}>
                    {label}
                  </div>
                ))}
              </OPopper>
            )}
          </div>
        </div>

        {peer_msg && (
          <div className="message-bubble-body-container">
            {isCalculatorMessage ? (
              <div className="message-bubble-body">
                <div className="calculator-message-content">
                  <div className="calculator-message-indicator">shared via calculator</div>
                  {calculatorContent()}
                </div>
              </div>
            ) : (
              <p
                className={`message-bubble-body ${isFocused || isEditingBubble ? 'active-message' : ''} ${
                  isPinned ? 'active-pinned-message' : ''
                }`}
              >
                {peer_msg}
              </p>
            )}
          </div>
        )}
        {file && (
          <AttachFile
            messageFile={file}
            fileType={file.fileExtension || file.fileType}
            isSent
            onViewFile={fileHandlers.view}
            onDownloadFile={fileHandlers.download}
            onDeleteClick={() => handleDeleteFileAttachment(message?.chatMessageId || '')}
            canDelete={!!canDeleteAttachment}
          />
        )}
      </div>

      <BottomSheetDrawer
        title="Actions"
        open={isMobileDevice && openActionsDrawer}
        onClose={toggleDrawer}
        actions={messageActions}
      />
    </div>
  );
};

export default MessageBubble;
